import * as ko from 'knockout';
import page from 'page';

import { session } from './session';
import { loadFailed } from './screens/base_loading_screen';
import { FormsStackController } from './components/forms_stack';
import { NavGroup } from './components/navigation_bar';

const ENGLISH_LANGUAGE_CODE = 'en';

class AppViewModel {
  productName = SERVER_INFO.PRODUCT_NAME;

  currentPage = ko.observable<{ name: string; title?: string; params?: {} }>(null);

  authorized = ko.pureComputed(() => {
    return session.authorized();
  });
  showNavigationBar = ko.observable(true);

  tenantName = ko.pureComputed(() => {
    return session.tenant() ? session.tenant().name : '';
  });

  formsStackController = new FormsStackController();

  thirdPartyCookiesError = ko.observable(false);
  showLoggedOut = ko.observable(false);
  showServerError = ko.observable<(retry: boolean) => void>(null);
  showNetworkError = ko.observable<(retry: boolean) => void>(null);
  showEntityNotFound = ko.observable(false);
  formSubmissionErrorModalVisibility = ko.observable(false);
  showTourOverlay = ko.observable(false);
  formSubmissionErrorModalMessage = ko.observable<string>('');
  formSubmissionErrorModalReload = ko.observable(true);

  private getMenu: () => NavGroup[];

  initMenu(getMenu: () => NavGroup[]) {
    this.getMenu = getMenu;
  }

  confirmLoggedOut = () => {
    session.logout().then(() => {
      this.showLoggedOut(false);
      location.href = location.href; // reload the page to login again
    });
  };

  retryServerError = () => {
    this.showServerError()(true);
    this.showServerError(null);
  };

  closeServerError = () => {
    this.showServerError()(false);
    this.showServerError(null);
  };

  retryNetworkError = () => {
    this.showNetworkError()(true);
    this.showNetworkError(null);
  };

  closeNetworkError = () => {
    this.showNetworkError()(false);
    this.showNetworkError(null);
  };

  closeEntityNotFound = () => {
    this.showEntityNotFound(false);
    page.redirect(session.toTenantPath('/'));
  };

  /**
   * Shows the form-submission error modal.
   *
   * @param message Text to display in the modal body.
   * @param options Modal options.
   * @param options.reload Whether the close button reloads the page. Defaults to
   *   `true` (legacy behavior). Pass `false` for recoverable validation errors
   *   where the user should be able to retry without losing dialog state.
   */
  showFormSubmissionErrorModal = (message: string, options: { reload?: boolean } = {}) => {
    this.formSubmissionErrorModalMessage(message);
    this.formSubmissionErrorModalReload(options.reload ?? true);
    this.formSubmissionErrorModalVisibility(true);
  };

  closeFormSubmissionErrorModal = () => {
    if (this.formSubmissionErrorModalReload()) {
      page.stop();
      location.href = location.href;
    }
    this.formSubmissionErrorModalVisibility(false);
  };

  closeThirdPartyCookiesError = () => {
    page.stop();
    location.href = location.href;
  };

  renewSubscription = () => {
    // page.redirect doesn't play nicely here
    location.href = session.toTenantPath('/subscription/');
  };

  menu = ko.pureComputed(() => this.getMenu());
}

export let app = new AppViewModel();

// Each navigation starts a fresh load, so the previous screen's failure must not
// leak into the next screen's loading indicator.
app.currentPage.subscribe(() => loadFailed(false));

export const RTL_LANGUAGES = ['ar'];
const userLanguage = localStorage.getItem('userLanguage');

export function setupApp(getMenu: () => NavGroup[]) {
  if (RTL_LANGUAGES.includes(userLanguage)) {
    document.body.style.direction = 'rtl';
    document.dir = 'rtl';
  } else {
    document.dir = 'ltr';
  }
  if (!localStorage.getItem('userLanguage')) {
    const supportedBrowserLanguage = SERVER_INFO.LANGUAGE_LIST.find((language) =>
      navigator.language.toLowerCase().includes(language.code)
    );
    localStorage.setItem(
      'userLanguage',
      supportedBrowserLanguage ? supportedBrowserLanguage.code : ENGLISH_LANGUAGE_CODE
    );
  }
  app.initMenu(getMenu);
  ko.applyBindings(app, document.body);
}
